import { useRef, useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { getApiUrl, fetchWithAuth } from '@/api'
import ImageUploadModal from '@/components/modal/ImageUploadModal'
import { parseProgramToKioskSections } from '@/utils/ProgramToKioskContent'

// ─── Types ───────────────────────────────────────────────────────────────────

interface UploadedImage {
  url: string
  order_num: number
}

interface SessionItem {
  session_name: string
  run_start: string
  run_end: string
  capacity: string
  order_num: number
}

interface FormState {
  title: string
  description: string
  target: string
  location: string
  apply_start: string
  apply_end: string
  run_start: string
  run_end: string
  schedule_text: string
  capacity: string
  has_sessions: boolean
  accepts_online_application: number
  sessions: SessionItem[]
  is_visible: number
  allow_member: number
  allow_guest: number
  require_name: number
  require_phone: number
  require_email: number
  require_affiliation: number
  require_grade: number
  consent_policy_id: string
}

interface ConsentPolicyItem {
  id: number
  consent_type: string
  title: string
  version: string | null
  is_active: number
}

const EMPTY_FORM: FormState = {
  title: '',
  description: '',
  target: '',
  location: '',
  apply_start: '',
  apply_end: '',
  run_start: '',
  run_end: '',
  schedule_text: '',
  capacity: '0',
  has_sessions: false,
  accepts_online_application: 1,
  sessions: [],
  is_visible: 1,
  allow_member: 1,
  allow_guest: 0,
  require_name: 1,
  require_phone: 1,
  require_email: 0,
  require_affiliation: 0,
  require_grade: 0,
  consent_policy_id: '2',
}

const EMPTY_SESSION = {
  session_name: '',
  run_start: '',
  run_end: '',
  capacity: '0',
  order_num: 0,
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const toDatetimeLocal = (isoStr: string | null | undefined) => {
  if (!isoStr) return ''
  return isoStr.slice(0, 16)
}

const inputCls = 'w-full border border-[#e8e0dc] rounded-lg px-3 py-2 text-[13px] text-[#2a1f1a] outline-none focus:border-[#887267] transition-colors bg-white'

function SectionTitle({ children }: { children: React.ReactNode }) {
  return <h2 className="text-[13px] font-bold text-[#5a3d32]">{children}</h2>
}

function Field({ label, children, full, hint }: { label: string; children: React.ReactNode; full?: boolean; hint?: string }) {
  return (
    <div className={`flex flex-col gap-1 ${full ? 'sm:col-span-2' : ''}`}>
      <label className="text-[11px] text-[#5a3d32] font-medium">{label}</label>
      {children}
      {hint && <p className="text-[11px] text-gray-400">{hint}</p>}
    </div>
  )
}

function ToggleRow({ label, checked, onChange }: { label: string; checked: boolean; onChange: (v: number) => void }) {
  return (
    <div className="flex items-center justify-between py-2.5 border-b border-[#f5f2f0] last:border-0">
      <span className="text-[13px] text-[#2a1f1a]">{label}</span>
      <button
        type="button"
        onClick={() => onChange(checked ? 0 : 1)}
        className={`relative w-10 h-5 rounded-full transition-colors cursor-pointer border-none ${checked ? 'bg-[#887267]' : 'bg-gray-200'}`}
      >
        <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all ${checked ? 'left-5' : 'left-0.5'}`} />
      </button>
    </div>
  )
}

function Card({ children }: { children: React.ReactNode }) {
  return (
    <div className="bg-white rounded-2xl border border-[#e8e0dc] px-6 py-6 shadow-[0_2px_12px_rgba(136,114,103,0.06)]">
      {children}
    </div>
  )
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function ProgramForm() {
  const { program_id } = useParams<{ program_id: string }>()
  const navigate = useNavigate()
  const isEdit = !!program_id

  const [form, setForm] = useState<FormState>(EMPTY_FORM)
  const [images, setImages] = useState<UploadedImage[]>([])   // ← 추가
  const [imageModalOpen, setImageModalOpen] = useState(false)  // ← 추가
  const [uploadingImage, setUploadingImage] = useState(false)  // ← 추가
  const [loading, setLoading] = useState(isEdit)
  const [saving, setSaving] = useState(false)
  const [consentPolicies, setConsentPolicies] = useState<ConsentPolicyItem[]>([])

  useEffect(() => {
    if (isEdit) fetchProgram()
  }, [program_id])

  useEffect(() => {
    fetchConsentPolicy()
  }, [])

  const fetchConsentPolicy = async () => {
    try {
      const res = await fetchWithAuth(getApiUrl('/marubit/api/consent-policy/list'))
      if (!res.ok) return
      const data = await res.json()
      setConsentPolicies(data.policies)
    } catch { /* 무시 */ }
  }

  const fetchProgram = async () => {
    setLoading(true)
    try {
      const res = await fetchWithAuth(getApiUrl(`/marubit/api/program/admin/${program_id}/detail`))
      if (!res.ok) throw new Error('조회 실패')
      const data = await res.json()
      const setting = data.application_setting

      setForm({
        title: data.title ?? '',
        description: data.description ?? '',
        target: data.target ?? '',
        location: data.location ?? '',
        apply_start: toDatetimeLocal(data.apply_start),
        apply_end: toDatetimeLocal(data.apply_end),
        run_start: toDatetimeLocal(data.run_start),
        run_end: toDatetimeLocal(data.run_end),
        schedule_text: data.schedule_text ?? '',
        capacity: String(data.capacity ?? 0),
        has_sessions: data.has_sessions ?? false,
        accepts_online_application: data.accepts_online_application ?? 1,
        sessions: (data.sessions ?? []).map((s: {
          session_name: string | null
          run_start: string
          run_end: string
          capacity: number
          order_num: number
        }) => ({
          session_name: s.session_name ?? '',
          run_start: toDatetimeLocal(s.run_start),
          run_end: toDatetimeLocal(s.run_end),
          capacity: String(s.capacity),
          order_num: s.order_num,
        })),
        is_visible: data.is_visible ?? 1,
        allow_member: setting?.allow_member ?? 1,
        allow_guest: setting?.allow_guest ?? 0,
        require_name: setting?.require_name ?? 1,
        require_phone: setting?.require_phone ?? 1,
        require_email: setting?.require_email ?? 0,
        require_affiliation: setting?.require_affiliation ?? 0,
        require_grade: setting?.require_grade ?? 0,
        consent_policy_id: String(setting?.consent_policy_id ?? ''),
      })

      // ← 수정 시 기존 이미지 로드
      setImages(
        (data.images ?? []).map((img: { url: string; order_num: number }) => ({
          url: img.url,
          order_num: img.order_num,
        }))
      )
    } catch {
      alert('프로그램 정보를 불러오는 데 실패했습니다.')
      navigate('/admin/program')
    } finally {
      setLoading(false)
    }
  }

  const set = (field: keyof FormState, value: unknown) =>
    setForm(prev => ({ ...prev, [field]: value }))

  // ─── Session handlers ─────────────────────────────────────────────────────

  const addSession = () => {
    setForm(prev => ({
      ...prev,
      sessions: [...prev.sessions, { ...EMPTY_SESSION, order_num: prev.sessions.length }],
    }))
  }

  const removeSession = (idx: number) => {
    setForm(prev => ({
      ...prev,
      sessions: prev.sessions
        .filter((_, i) => i !== idx)
        .map((s, i) => ({ ...s, order_num: i })),
    }))
  }

  const updateSession = (idx: number, field: keyof SessionItem, value: string) => {
    setForm(prev => ({
      ...prev,
      sessions: prev.sessions.map((s, i) => i === idx ? { ...s, [field]: value } : s),
    }))
  }

  // ─── Image handlers ───────────────────────────────────────────────────────

  const handleImageFileSelect = async (file: File) => {
    setUploadingImage(true)
    try {
      const formData = new FormData()
      formData.append('file', file)
      const res = await fetchWithAuth(
        getApiUrl('/marubit/api/program/admin/upload-image'),
        { method: 'POST', body: formData }
      )
      if (!res.ok) { const err = await res.json(); throw new Error(err.detail ?? '업로드 실패') }
      const data = await res.json()
      setImages(prev => [...prev, { url: data.url, order_num: prev.length }])
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : '이미지 업로드에 실패했습니다.')
    } finally {
      setUploadingImage(false)
    }
  }

  const removeImage = (idx: number) => {
    setImages(prev =>
      prev.filter((_, i) => i !== idx).map((img, i) => ({ ...img, order_num: i }))
    )
  }

  const moveImage = (idx: number, dir: -1 | 1) => {
    const next = idx + dir
    if (next < 0 || next >= images.length) return
    setImages(prev => {
      const arr = [...prev]
      ;[arr[idx], arr[next]] = [arr[next], arr[idx]]
      return arr.map((img, i) => ({ ...img, order_num: i }))
    })
  }

  // ─── Submit ───────────────────────────────────────────────────────────────

  const handleSubmit = async () => {
    if (!form.title.trim()) { alert('프로그램명을 입력해주세요.'); return }
    if (!form.target.trim()) { alert('대상을 입력해주세요.'); return }
    if (!form.location.trim()) { alert('장소를 입력해주세요.'); return }
    if (!!form.apply_start !== !!form.apply_end) {
      alert('신청 기간은 시작/종료를 함께 입력하거나 둘 다 비워주세요.'); return
    }
    if (!form.has_sessions && (!!form.run_start !== !!form.run_end)) {
      alert('운영 기간은 시작/종료를 함께 입력하거나 둘 다 비워주세요.'); return
    }
    // 기간을 비운 경우(매주 목요일 등) 운영 안내 문구는 필수
    if (!form.has_sessions && !form.run_start && !form.schedule_text.trim()) {
      alert('운영 기간을 비운 경우 운영 안내 문구를 입력해주세요. (예: 매주 목요일 15:00~17:00)'); return
    }
    if (form.has_sessions && form.sessions.length === 0) {
      alert('회차를 최소 1개 이상 추가해주세요.'); return
    }
    if (form.accepts_online_application === 1 && form.allow_member === 0 && form.allow_guest === 0) {
      alert('회원 또는 비회원 중 최소 1개는 신청 허용해야 합니다.'); return
    }
    if (!form.description.trim() && images.length === 0) {
      alert('프로그램 설명 또는 이미지 중 최소 1개는 입력해주세요.'); return
    }
    if (!window.confirm(isEdit ? '수정 내용을 저장하시겠습니까?' : '프로그램을 등록하시겠습니까?')) return

    setSaving(true)
    try {
      const body = {
        title: form.title,
        description: form.description || null,
        target: form.target,
        location: form.location,
        apply_start: form.apply_start || null,
        apply_end: form.apply_end || null,
        run_start: !form.has_sessions ? (form.run_start || null) : null,
        run_end: !form.has_sessions ? (form.run_end || null) : null,
        schedule_text: form.schedule_text.trim() || null,
        capacity: !form.has_sessions ? Number(form.capacity) : null,
        has_sessions: form.has_sessions,
        accepts_online_application: form.accepts_online_application,
        sessions: form.has_sessions
          ? form.sessions.map(s => ({
              session_name: s.session_name || null,
              run_start: s.run_start,
              run_end: s.run_end,
              capacity: Number(s.capacity),
              order_num: s.order_num,
            }))
          : [],
        images: images.map(img => ({ url: img.url, order_num: img.order_num })), // ← 실제 전송
        files: [],
        is_visible: form.is_visible,
        application_setting: {
          // 온라인 신청을 받지 않으면 신청 설정 카드가 숨겨지므로,
          // 둘 다 0이어서 서버 검증에 걸리지 않도록 회원 허용을 기본값으로 보정
          allow_member:
            form.accepts_online_application === 0 && form.allow_member === 0 && form.allow_guest === 0
              ? 1
              : form.allow_member,
          allow_guest: form.allow_guest,
          require_name: form.require_name,
          require_phone: form.require_phone,
          require_email: form.require_email,
          require_affiliation: form.require_affiliation,
          require_grade: form.require_grade,
          consent_policy_id: form.consent_policy_id ? Number(form.consent_policy_id) : null,
        },
      }

      const res = await fetchWithAuth(
        getApiUrl(isEdit ? `/marubit/api/program/admin/${program_id}` : '/marubit/api/program/admin'),
        {
          method: isEdit ? 'PUT' : 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        }
      )
      if (!res.ok) { const err = await res.json(); throw new Error(err.detail ?? '저장 실패') }

      alert(isEdit ? '수정되었습니다.' : '프로그램이 등록되었습니다.')
      navigate('/admin/program')
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : '저장에 실패했습니다.')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="bg-[#f5f2f0] min-h-screen p-8 flex items-center justify-center">
        <p className="text-gray-300 text-[13px]">불러오는 중...</p>
      </div>
    )
  }

  return (
    <div className="bg-[#f5f2f0] min-h-screen p-8">
      <div className="max-w-[1200px] mx-auto">

        {/* Header */}
        <div className="flex items-start justify-between mb-6">
          <div>
            <h1 className="text-[22px] font-extrabold text-[#2a1f1a] mb-1">
              {isEdit ? '프로그램 수정' : '프로그램 등록'}
            </h1>
            <p className="text-[13px] text-gray-400">
              {new Date().toLocaleDateString('ko-KR', {
                year: 'numeric', month: 'long', day: 'numeric', weekday: 'long',
              })}
            </p>
          </div>
          <button
            onClick={() => navigate('/admin/program')}
            className="text-[13px] text-[#887267] font-semibold px-4 py-2 rounded-lg border border-[#e8d8ce] bg-white cursor-pointer hover:bg-[#f5f2f0] transition-colors"
          >← 목록으로</button>
        </div>

        <div className="flex flex-col gap-5">

          {/* ── 기본 정보 ── */}
          <Card>
            <SectionTitle>기본 정보</SectionTitle>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
              <Field label="프로그램명 *" full>
                <input value={form.title} onChange={e => set('title', e.target.value)} placeholder="프로그램 이름" className={inputCls} />
              </Field>
              <Field label="대상 *">
                <input value={form.target} onChange={e => set('target', e.target.value)} placeholder="예: 관내 9세-12세 청소년" className={inputCls} />
              </Field>
              <Field label="장소 *">
                <input value={form.location} onChange={e => set('location', e.target.value)} placeholder="예: 3층 강당" className={inputCls} />
              </Field>
              <Field label="노출 여부">
                <select value={form.is_visible} onChange={e => set('is_visible', Number(e.target.value))} className={inputCls}>
                  <option value={1}>노출</option>
                  <option value={0}>숨김</option>
                </select>
              </Field>
              <Field label="온라인 신청 여부">
                <select
                  value={form.accepts_online_application}
                  onChange={e => set('accepts_online_application', Number(e.target.value))}
                  className={inputCls}
                >
                  <option value={1}>유 — 홈페이지/키오스크에서 신청 접수</option>
                  <option value={0}>무 — 안내만 (전화·문자 등으로 신청)</option>
                </select>
              </Field>
              <Field label="프로그램 설명" full>
                <textarea
                  value={form.description}
                  onChange={e => set('description', e.target.value)}
                  placeholder="프로그램에 대한 설명을 입력해주세요."
                  rows={4}
                  className="w-full border border-[#e8e0dc] rounded-lg px-3 py-2 text-[13px] text-[#2a1f1a] outline-none focus:border-[#887267] transition-colors resize-none bg-white"
                />
              </Field>
            </div>
          </Card>

          {/* ── 이미지 ── */}
          <Card>
            <div className="flex items-center justify-between mb-4">
              <SectionTitle>이미지</SectionTitle>
              <button
                onClick={() => setImageModalOpen(true)}
                disabled={uploadingImage}
                className="text-[12px] text-[#887267] font-semibold px-4 py-1.5 rounded-lg border border-[#e8d8ce] bg-white cursor-pointer hover:bg-[#f5f2f0] transition-colors disabled:opacity-50"
              >
                {uploadingImage ? '업로드 중...' : '+ 이미지 추가'}
              </button>
            </div>

            {images.length === 0 ? (
              <p className="text-[12px] text-gray-300 py-4 text-center">등록된 이미지가 없습니다.</p>
            ) : (
              <div className="flex flex-col gap-2">
                {images.map((img, idx) => (
                  <div key={idx} className="flex items-center gap-3 bg-[#fdf8f0] rounded-xl border border-[#e8e0dc] px-4 py-3">
                    {/* 순서 번호 */}
                    <span className="text-[11px] text-[#5a3d32] font-semibold w-5 text-center shrink-0">{idx + 1}</span>
                    {/* 썸네일 */}
                    <img src={img.url} alt={`이미지 ${idx + 1}`} className="w-16 h-12 object-cover rounded-lg border border-[#e8e0dc] shrink-0" />
                    {/* URL */}
                    <span className="text-[11px] text-gray-400 truncate flex-1">{img.url.split('/').pop()}</span>
                    {/* 순서 버튼 */}
                    <div className="flex flex-col gap-0.5 shrink-0">
                      <button
                        onClick={() => moveImage(idx, -1)}
                        disabled={idx === 0}
                        className="text-[10px] text-[#887267] px-1.5 py-0.5 rounded border border-[#e8d8ce] bg-white hover:bg-[#f5f2f0] disabled:opacity-30 cursor-pointer transition-colors"
                      >▲</button>
                      <button
                        onClick={() => moveImage(idx, 1)}
                        disabled={idx === images.length - 1}
                        className="text-[10px] text-[#887267] px-1.5 py-0.5 rounded border border-[#e8d8ce] bg-white hover:bg-[#f5f2f0] disabled:opacity-30 cursor-pointer transition-colors"
                      >▼</button>
                    </div>
                    {/* 삭제 */}
                    <button
                      onClick={() => removeImage(idx)}
                      className="text-[12px] text-red-400 hover:text-red-600 border border-red-100 rounded px-2 py-1 bg-white cursor-pointer transition-colors shrink-0"
                    >삭제</button>
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* ── 신청 기간 ── */}
          <Card>
            <SectionTitle>신청 기간</SectionTitle>
            <p className="text-[12px] text-gray-400 mt-1">
              정해진 신청 기간이 없으면 비워두세요. (상시 접수로 처리되며 마감 표시가 되지 않습니다)
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
              <Field label="신청 시작일시">
                <input type="datetime-local" value={form.apply_start} onChange={e => set('apply_start', e.target.value)} className={inputCls} />
              </Field>
              <Field label="신청 종료일시">
                <input type="datetime-local" value={form.apply_end} onChange={e => set('apply_end', e.target.value)} className={inputCls} />
              </Field>
            </div>
            {(form.apply_start || form.apply_end) && (
              <button
                type="button"
                onClick={() => { set('apply_start', ''); set('apply_end', '') }}
                className="mt-3 text-[12px] text-[#887267] border border-[#e8d8ce] rounded px-2 py-1 bg-white hover:bg-[#f5f2f0] cursor-pointer transition-colors"
              >
                신청 기간 비우기
              </button>
            )}
          </Card>

          {/* ── 운영 일정 ── */}
          <Card>
            <div className="flex items-center justify-between">
              <SectionTitle>운영 일정</SectionTitle>
              <label className="flex items-center gap-2 cursor-pointer">
                <span className="text-[12px] text-[#5a3d32] font-medium">회차 운영</span>
                <button
                  type="button"
                  onClick={() => {
                    set('has_sessions', !form.has_sessions)
                    if (!form.has_sessions && form.sessions.length === 0) addSession()
                  }}
                  className={`relative w-10 h-5 rounded-full transition-colors cursor-pointer border-none ${form.has_sessions ? 'bg-[#887267]' : 'bg-gray-200'}`}
                >
                  <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all ${form.has_sessions ? 'left-5' : 'left-0.5'}`} />
                </button>
              </label>
            </div>

            {!form.has_sessions ? (
              <>
                <p className="text-[12px] text-gray-400 mt-1">
                  운영 기간이 정해지지 않았다면 날짜를 비우고 아래 운영 안내에 적어주세요. (예: 매주 목요일 15:00~17:00)
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4">
                  <Field label="운영 시작일시">
                    <input type="datetime-local" value={form.run_start} onChange={e => set('run_start', e.target.value)} className={inputCls} />
                  </Field>
                  <Field label="운영 종료일시">
                    <input type="datetime-local" value={form.run_end} onChange={e => set('run_end', e.target.value)} className={inputCls} />
                  </Field>
                  <Field label="정원" hint="0 입력 시 무제한">
                    <input type="number" min={0} value={form.capacity} onChange={e => set('capacity', e.target.value)} className={inputCls} />
                  </Field>
                </div>
                {(form.run_start || form.run_end) && (
                  <button
                    type="button"
                    onClick={() => { set('run_start', ''); set('run_end', '') }}
                    className="mt-3 text-[12px] text-[#887267] border border-[#e8d8ce] rounded px-2 py-1 bg-white hover:bg-[#f5f2f0] cursor-pointer transition-colors"
                  >
                    운영 기간 비우기
                  </button>
                )}
              </>
            ) : (
              <div className="mt-4 flex flex-col gap-3">
                {form.sessions.length === 0 ? (
                  <p className="text-[12px] text-gray-300 py-2">회차를 추가해주세요.</p>
                ) : form.sessions.map((s, idx) => (
                  <div key={idx} className="bg-[#fdf8f0] rounded-xl border border-[#e8e0dc] px-4 py-4">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-[12px] font-semibold text-[#5a3d32]">{idx + 1}회차</span>
                      <button onClick={() => removeSession(idx)} className="text-[12px] text-red-400 hover:text-red-600 border border-red-100 rounded px-2 py-1 bg-white cursor-pointer transition-colors">삭제</button>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                      <Field label="회차명">
                        <input value={s.session_name} onChange={e => updateSession(idx, 'session_name', e.target.value)} placeholder="예: 1회차" className={inputCls} />
                      </Field>
                      <Field label="시작일시 *">
                        <input type="datetime-local" value={s.run_start} onChange={e => updateSession(idx, 'run_start', e.target.value)} className={inputCls} />
                      </Field>
                      <Field label="종료일시 *">
                        <input type="datetime-local" value={s.run_end} onChange={e => updateSession(idx, 'run_end', e.target.value)} className={inputCls} />
                      </Field>
                      <Field label="정원" hint="0 입력 시 무제한">
                        <input type="number" min={0} value={s.capacity} onChange={e => updateSession(idx, 'capacity', e.target.value)} className={inputCls} />
                      </Field>
                    </div>
                  </div>
                ))}
                <button onClick={addSession} className="text-[12px] text-[#887267] font-semibold px-4 py-2.5 rounded-xl border border-[#e8d8ce] bg-white cursor-pointer hover:bg-[#f5f2f0] transition-colors w-full">+ 회차 추가</button>
              </div>
            )}

            <div className="mt-4 pt-4 border-t border-[#f5f2f0]">
              <Field label="운영 안내 문구" hint="날짜로 표현하기 어려운 일정을 자유롭게 입력 (예: 매주 목요일 15:00~17:00)" full>
                <input
                  value={form.schedule_text}
                  onChange={e => set('schedule_text', e.target.value)}
                  placeholder="예: 매주 목요일 15:00~17:00 / 상시 운영"
                  maxLength={200}
                  className={inputCls}
                />
              </Field>
            </div>
          </Card>

          {/* ── 신청 설정 (온라인 신청을 받는 경우에만) ── */}
          {form.accepts_online_application === 0 ? (
            <Card>
              <SectionTitle>신청 설정</SectionTitle>
              <p className="text-[12px] text-gray-400 mt-2">
                온라인 신청을 받지 않는 프로그램이라 신청 설정이 필요하지 않습니다.
                안내 페이지로만 노출되며, 신청은 전화·문자 등으로 받습니다.
              </p>
            </Card>
          ) : (
          <Card>
            <SectionTitle>신청 설정</SectionTitle>
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div className="flex flex-col gap-4">
                <div>
                  <p className="text-[11px] text-[#5a3d32] font-medium mb-2">신청 허용 대상</p>
                  <div className="bg-[#fdf8f0] rounded-xl border border-[#e8e0dc] px-4 py-1">
                    <ToggleRow label="회원 신청 허용" checked={form.allow_member === 1} onChange={v => set('allow_member', v)} />
                    <ToggleRow label="비회원 신청 허용" checked={form.allow_guest === 1} onChange={v => set('allow_guest', v)} />
                  </div>
                </div>
                <Field label="개인정보 이용동의 정책" hint="미선택 시 동의 없음">
                  <select value={form.consent_policy_id} onChange={e => set('consent_policy_id', e.target.value)} className={inputCls}>
                    <option value="">선택 안함</option>
                    {consentPolicies.map(p => (
                      <option key={p.id} value={String(p.id)}>
                        {p.title} ({p.consent_type}{p.version ? ` v${p.version}` : ''})
                      </option>
                    ))}
                  </select>
                </Field>
              </div>
              <div>
                <p className="text-[11px] text-[#5a3d32] font-medium mb-2">필수 입력 항목</p>
                <div className="bg-[#fdf8f0] rounded-xl border border-[#e8e0dc] px-4 py-1">
                  <ToggleRow label="이름" checked={form.require_name === 1} onChange={v => set('require_name', v)} />
                  <ToggleRow label="전화번호" checked={form.require_phone === 1} onChange={v => set('require_phone', v)} />
                  <ToggleRow label="이메일" checked={form.require_email === 1} onChange={v => set('require_email', v)} />
                  <ToggleRow label="소속(학교)" checked={form.require_affiliation === 1} onChange={v => set('require_affiliation', v)} />
                  <ToggleRow label="학년" checked={form.require_grade === 1} onChange={v => set('require_grade', v)} />
                </div>
              </div>
            </div>
          </Card>
          )}

          {/* ── Actions ── */}
          <div className="flex justify-end gap-2 pb-4">
            {isEdit && (
              <button
                onClick={() => {
                  const { sections, firstImageUrl } = parseProgramToKioskSections(form, images)
                  navigate(`/admin/kiosk/content/register?kiosk_code=${localStorage.getItem('kiosk_code')}`, {
                    state: { title: form.title, sections, firstImageUrl },
                  })
                }}
                className="text-[13px] text-[#887267] font-semibold px-5 py-2.5 rounded-lg border border-[#887267] bg-white cursor-pointer hover:bg-[#f5f2f0] transition-colors"
              >
                키오스크 등록
              </button>
            )}
            <button onClick={() => navigate('/admin/program')} className="text-[13px] text-[#887267] font-semibold px-5 py-2.5 rounded-lg border border-[#e8d8ce] bg-white cursor-pointer hover:bg-[#f5f2f0] transition-colors">취소</button>
            <button
              onClick={handleSubmit}
              disabled={saving}
              className="bg-[#887267] text-white text-[13px] font-semibold px-6 py-2.5 rounded-lg border-none cursor-pointer hover:bg-[#6b5248] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? '저장 중...' : isEdit ? '수정하기' : '등록하기'}
            </button>
          </div>

        </div>
      </div>

      {/* 이미지 업로드 모달 */}
      <ImageUploadModal
        isOpen={imageModalOpen}
        onClose={() => setImageModalOpen(false)}
        onSelectFile={handleImageFileSelect}
      />
    </div>
  )
}